import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Tag, Sparkles, Mail, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

const COUPON_CODE = "UPLEVEL10";
const SESSION_KEY = "ru_popup_dismissed";
const LANG_KEY = "langPref";

export const RuEmailCapturePopup = ({ cityName = "вашем городе", delay = 28000 }: { cityName?: string; delay?: number }) => {
  const { toast } = useToast();
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    // Only show for Russian language users
    const lang = localStorage.getItem(LANG_KEY);
    if (lang !== "ru") return;
    if (sessionStorage.getItem(SESSION_KEY)) return;

    const timer = setTimeout(() => setVisible(true), delay);
    const handleMouseOut = (e: MouseEvent) => {
      if (e.clientY <= 5 && !sessionStorage.getItem(SESSION_KEY)) {
        clearTimeout(timer);
        setVisible(true);
      }
    };
    document.addEventListener("mouseleave", handleMouseOut);
    return () => { clearTimeout(timer); document.removeEventListener("mouseleave", handleMouseOut); };
  }, [delay]);

  const dismiss = () => {
    setClosing(true);
    setTimeout(() => { setVisible(false); setClosing(false); sessionStorage.setItem(SESSION_KEY, "1"); }, 300);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast({ title: "Неверный email", description: "Пожалуйста, введите корректный адрес электронной почты.", variant: "destructive" });
      return;
    }

    fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
      method: "POST",
      headers: { "Content-Type": "application/json", "version": "2021-07-28" },
      body: JSON.stringify({
        type: "external_form_submission",
        timestamp: Date.now(),
        formId: "RU Email Capture Popup",
        formData: { email },
        url: window.location.href,
        title: document.title,
        path: window.location.pathname,
        userAgent: navigator.userAgent,
        trackingId: "tk_12d30557e33f4e29be39cf7c348f6d99",
        locationId: "M7JmlFFXdtFNLwyfY61D"
      })
    }).catch(() => {});

    setSubmitted(true);
    sessionStorage.setItem(SESSION_KEY, "1");
  };

  const handleCopyCode = async () => {
    await navigator.clipboard.writeText(COUPON_CODE);
    setCodeCopied(true);
    toast({ title: "Код скопирован!", description: `Используйте ${COUPON_CODE} при бронировании.` });
    setTimeout(() => setCodeCopied(false), 3000);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className={cn(
        "relative w-full max-w-md rounded-3xl overflow-hidden shadow-2xl transition-all duration-300",
        closing ? "opacity-0 scale-95 translate-y-4" : "opacity-100 scale-100 translate-y-0"
      )}>
        <div className="h-1.5 w-full bg-gradient-to-r from-primary via-primary/60 to-primary bg-[length:200%_auto]" />
        <div className="bg-card p-8">
          <button onClick={dismiss} className="absolute top-4 right-4 h-8 w-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors" aria-label="Закрыть">
            <X className="h-4 w-4" />
          </button>

          {!submitted ? (
            <>
              <div className="flex justify-center mb-5">
                <div className="relative">
                  <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center">
                    <Tag className="h-10 w-10 text-primary" />
                  </div>
                  <div className="absolute -top-1 -right-1 h-7 w-7 rounded-full bg-green-500 flex items-center justify-center">
                    <span className="text-white text-xs font-bold">%</span>
                  </div>
                </div>
              </div>
              <h2 className="text-2xl font-extrabold text-center mb-2 leading-tight">
                Подождите! Получите{" "}
                <span className="text-primary">скидку 10%</span>
                <br />на уборку в {cityName}
              </h2>
              <p className="text-muted-foreground text-center text-sm mb-6">
                Введите email и мы пришлём вам эксклюзивный промокод. Предложение ограничено по времени.
              </p>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input type="email" placeholder="ваш@email.com" value={email} onChange={e => setEmail(e.target.value)} className="pl-10 h-12 rounded-xl" required />
                </div>
                <Button type="submit" size="lg" className="w-full h-12 font-bold bg-primary text-primary-foreground hover:bg-primary/90 btn-pulse">
                  <Sparkles className="h-4 w-4 mr-2" />
                  Получить купон на 10%
                </Button>
              </form>
              <p className="text-center text-xs text-muted-foreground mt-4">🔒 Без спама. Отписаться можно в любое время.</p>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="flex justify-center mb-5">
                <div className="h-20 w-20 rounded-full bg-green-500/10 flex items-center justify-center">
                  <CheckCircle2 className="h-10 w-10 text-green-500" />
                </div>
              </div>
              <h2 className="text-2xl font-extrabold mb-2">Готово! Проверьте почту 🎉</h2>
              <p className="text-muted-foreground text-sm mb-6">
                Мы отправили купон на <span className="font-semibold text-foreground">{email}</span>. Используйте код прямо сейчас:
              </p>
              <button onClick={handleCopyCode} className={cn(
                "w-full py-4 rounded-2xl border-2 border-dashed transition-all duration-200 font-mono font-extrabold text-3xl tracking-widest",
                codeCopied ? "border-green-500 bg-green-50 text-green-600" : "border-primary bg-primary/5 text-primary hover:bg-primary/10"
              )}>
                {codeCopied ? "✓ Скопировано!" : COUPON_CODE}
              </button>
              <p className="text-xs text-muted-foreground mt-2 mb-6">Нажмите для копирования · Применить при онлайн-бронировании</p>
              <Button asChild size="lg" className="w-full h-12 font-bold bg-primary text-primary-foreground hover:bg-primary/90">
                <a href="/book">Забронировать со скидкой 10%</a>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
